/**
 * Preview fonts and Phira's text metrics (W2-01a preview-gl; rendering.md R§3.6, R§7.1, DESIGN.md D29,
 * ARCHITECTURE.md §7.3).
 *
 * Phira lays text out with ab_glyph, whose "PxScale" is the distance ascent - descent of the font, not the em size.
 * Every size here is therefore a PxScale and the fonts are metric-normalised to it:
 *
 *  - `ui` (text lines, song name, level): Phira's `font.ttf` is not available; the bundled OFL stack is
 *    **Noto Sans SC -> Noto Sans** (CJK and kana subsets load on demand). The CSS font size that gives ascent - descent
 *    = PxScale is measured from the primary font once it is loaded (fontBoundingBoxAscent / Descent); until then
 *    Noto Sans SC's hhea numbers are used (ascender 1160, descender -288, upm 1000).
 *  - `pgr` (score, combo, "COMBO" / "AUTOPLAY"): Phira's `phigros.ttf` has USE_TYPO_METRICS with typo 800 / -200 over
 *    upm 1000, so its CSS size equals the PxScale and its ascent is 0.8 PxScale. Saira (OFL) is drawn at exactly
 *    those numbers, so digits sit on Phira's baseline with Phira's size.
 *
 * Text metrics are approximate by nature (D29): the fonts are not Phira's.
 *
 * Font loading: `ensure(role, text)` answers whether the faces needed for the string's code points are loaded
 * (`document.fonts.check`), and starts `document.fonts.load` for them when not. Every completed load (and every
 * `loadingdone` of the document, e.g. a subset another view requested) bumps `generation`; the text cache
 * re-rasterises entries made under an older generation and the renderer asks for a redraw.
 */

import '@fontsource/saira/latin-400.css';
import type { Unsubscribe } from '../../core/util/types';
import { HUD_EM, PGR_FONT_ASCENT, UI_FONT_ASCENT } from '../../core/engine/notes/hud';
import type { HudTextMeasurer } from '../../core/engine/notes/index';

export type FontRole = 'ui' | 'pgr';

/** CSS families of each role (the generic family is the last resort while a subset is still loading). */
export const FONT_FAMILIES: Readonly<Record<FontRole, string>> = {
  ui: '"Noto Sans SC", "Noto Sans", sans-serif',
  pgr: '"Saira", "Noto Sans", sans-serif',
};

/** Weight drawn for each role. */
export const FONT_WEIGHTS: Readonly<Record<FontRole, number>> = { ui: 400, pgr: 400 };

/**
 * Metrics of a role in PxScale units (ascent + descent = 1 by construction, line gap extra), plus the CSS font size
 * per PxScale pixel.
 */
export interface FontMetrics {
  readonly ascent: number;
  readonly descent: number;
  readonly lineGap: number;
  /** CSS font-size px per PxScale px. */
  readonly cssPerPx: number;
}

/** Noto Sans SC hhea metrics (ascender 1160, descender -288, line gap 0, upm 1000). */
export const UI_FALLBACK_METRICS: FontMetrics = {
  ascent: UI_FONT_ASCENT,
  descent: 1 - UI_FONT_ASCENT,
  lineGap: 0,
  cssPerPx: 1000 / 1448,
};

/** phigros.ttf (typo 800 / -200, upm 1000): CSS size = PxScale. */
export const PGR_METRICS: FontMetrics = {
  ascent: PGR_FONT_ASCENT,
  descent: 1 - PGR_FONT_ASCENT,
  lineGap: 0,
  cssPerPx: 1,
};

/** CSS `font` shorthand of a role at a CSS pixel size. */
export function fontCss(role: FontRole, cssPx: number): string {
  return `${String(FONT_WEIGHTS[role])} ${String(Math.max(0.5, cssPx))}px ${FONT_FAMILIES[role]}`;
}

/** A 2D context for measuring and rasterising text. */
export type Text2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
export type TextCanvas = OffscreenCanvas | HTMLCanvasElement;

/** Creates a canvas of the given size (OffscreenCanvas where available). */
export function createTextCanvas(w: number, h: number): TextCanvas {
  if (typeof OffscreenCanvas === 'function') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function isOffscreenCanvas(c: TextCanvas): c is OffscreenCanvas {
  return typeof OffscreenCanvas === 'function' && c instanceof OffscreenCanvas;
}

/** The 2D context of a text canvas (null when the environment has none). */
export function context2d(c: TextCanvas): Text2D | null {
  try {
    // Narrow first: `getContext` on the union resolves to the untyped overload (RenderingContext).
    return isOffscreenCanvas(c) ? c.getContext('2d') : c.getContext('2d');
  } catch {
    return null;
  }
}

/** Code points of a string (Phira counts Unicode scalar values). */
export function codePoints(text: string): string[] {
  return Array.from(text);
}

/** Reference PxScale used for measuring. */
const MEASURE_PX = 100;
/** Seconds after which a font load that has not settled is treated as done (offline: rasterise with fallbacks). */
const LOAD_TIMEOUT_MS = 3000;
/** Cached advance widths before the cache is cleared. */
const WIDTH_CACHE_MAX = 4096;

type FontSetLike = Pick<FontFaceSet, 'check' | 'load' | 'addEventListener' | 'removeEventListener'>;

function documentFonts(): FontSetLike | null {
  if (typeof document === 'undefined') return null;
  const f = (document as { fonts?: FontSetLike }).fonts;
  return f !== undefined && typeof f.check === 'function' && typeof f.load === 'function' ? f : null;
}

/**
 * Font state of one renderer: metrics per role, load tracking, advance-width measuring.
 * `fonts` is injectable for tests (null = no FontFaceSet: everything counts as loaded).
 */
export class PreviewFonts {
  private readonly fonts: FontSetLike | null;
  private readonly measureCtx: Text2D | null;
  private uiMetrics: FontMetrics = UI_FALLBACK_METRICS;
  private uiMeasuredAt = -1;
  /** Strings (per role) whose load is in flight, or settled. */
  private readonly loads = new Map<string, 'loading' | 'done'>();
  private readonly widths = new Map<string, number>();
  private readonly listeners = new Set<() => void>();
  private gen = 0;
  private disposed = false;
  private readonly onLoadingDone = (): void => {
    this.bump();
  };

  constructor(fonts: FontSetLike | null = documentFonts()) {
    this.fonts = fonts;
    this.measureCtx = context2d(createTextCanvas(8, 8));
    fonts?.addEventListener('loadingdone', this.onLoadingDone);
  }

  /** Bumps whenever a font face finished loading (text made earlier may have used a fallback face). */
  get generation(): number {
    return this.gen;
  }

  /** Called when fonts changed (re-render to pick up re-rasterised text). */
  onChange(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  private bump(): void {
    if (this.disposed) return;
    this.gen++;
    this.widths.clear();
    for (const cb of [...this.listeners]) cb();
  }

  /** Metrics of a role (the UI font is measured from the loaded primary face). */
  metrics(role: FontRole): FontMetrics {
    if (role === 'pgr') return PGR_METRICS;
    if (this.uiMeasuredAt !== this.gen) {
      this.uiMeasuredAt = this.gen;
      this.uiMetrics = this.measureUi() ?? UI_FALLBACK_METRICS;
    }
    return this.uiMetrics;
  }

  private measureUi(): FontMetrics | null {
    const ctx = this.measureCtx;
    if (ctx === null) return null;
    // Measure only once the primary face is there; a fallback face would give other numbers.
    if (this.fonts !== null && !this.safeCheck(`${String(MEASURE_PX)}px "Noto Sans SC"`, 'A')) return null;
    ctx.font = `${String(FONT_WEIGHTS.ui)} ${String(MEASURE_PX)}px ${FONT_FAMILIES.ui}`;
    const m = ctx.measureText('Hg');
    const asc = m.fontBoundingBoxAscent;
    const desc = m.fontBoundingBoxDescent;
    if (!(asc > 0) || !(desc >= 0) || !Number.isFinite(asc + desc)) return null;
    const total = asc + desc;
    return { ascent: asc / total, descent: desc / total, lineGap: 0, cssPerPx: MEASURE_PX / total };
  }

  private safeCheck(spec: string, text: string): boolean {
    try {
      return this.fonts?.check(spec, text) ?? true;
    } catch {
      return true;
    }
  }

  /**
   * Whether `text` can be rasterised in `role` with its real faces. Starts loading the faces of the string's code
   * points when needed and returns false meanwhile; a load that does not settle in 3 s counts as done.
   */
  ensure(role: FontRole, text: string): boolean {
    const fonts = this.fonts;
    if (fonts === null || text === '') return true;
    const key = `${role}\u0000${text}`;
    const state = this.loads.get(key);
    if (state === 'done') return true;
    const spec = fontCss(role, 32);
    if (this.safeCheck(spec, text)) {
      this.remember(key, 'done');
      return true;
    }
    if (state === 'loading') return false;
    this.remember(key, 'loading');
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      this.remember(key, 'done');
      this.bump();
    };
    const timer = setTimeout(finish, LOAD_TIMEOUT_MS);
    let p: Promise<unknown>;
    try {
      p = fonts.load(spec, text);
    } catch {
      p = Promise.resolve();
    }
    p.then(
      () => {
        clearTimeout(timer);
        finish();
      },
      () => {
        clearTimeout(timer);
        finish();
      },
    );
    return false;
  }

  private remember(key: string, state: 'loading' | 'done'): void {
    if (this.loads.size > WIDTH_CACHE_MAX && !this.loads.has(key)) {
      for (const [k, v] of this.loads) if (v === 'done') this.loads.delete(k);
    }
    this.loads.set(key, state);
  }

  /** Advance width of one line of text in PxScale units (0 without a measuring context). */
  advance(role: FontRole, text: string): number {
    if (text === '') return 0;
    const key = `${role}\u0000${text}`;
    const known = this.widths.get(key);
    if (known !== undefined) return known;
    const ctx = this.measureCtx;
    let w = 0;
    if (ctx !== null) {
      const fm = this.metrics(role);
      ctx.font = fontCss(role, MEASURE_PX * fm.cssPerPx);
      const width = ctx.measureText(text).width;
      w = Number.isFinite(width) && width > 0 ? width / MEASURE_PX : 0;
    }
    if (this.widths.size >= WIDTH_CACHE_MAX) this.widths.clear();
    this.widths.set(key, w);
    return w;
  }

  /**
   * HUD text width in UI units for the engine's legacy layout (CR-W1-06-2): advance at PxScale 0.08 * size.
   * Plain function (no `this` needed by the caller).
   */
  readonly measureHudText: HudTextMeasurer = (text, font, size) => this.advance(font, text) * HUD_EM * size;

  dispose(): void {
    this.disposed = true;
    this.fonts?.removeEventListener('loadingdone', this.onLoadingDone);
    this.listeners.clear();
  }
}

/** Splits a text-line string into its lines (Phira's multiline layout: explicit line breaks only). */
export function splitLines(text: string): string[] {
  return text.split(/\r\n|\r|\n/);
}

/**
 * Longest prefix of `text` that fits `maxWidth` (PxScale units) with an ellipsis appended (Phira's `max_width`
 * truncation). The text itself when it fits.
 */
export function truncateToWidth(text: string, maxWidth: number, width: (s: string) => number): string {
  if (!(maxWidth > 0) || width(text) <= maxWidth) return text;
  const cps = codePoints(text);
  let lo = 0;
  let hi = cps.length;
  // Largest n with width(prefix(n) + ellipsis) <= maxWidth.
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (width(`${cps.slice(0, mid).join('')}…`) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return `${cps.slice(0, lo).join('')}…`;
}
